/* eslint-disable no-console */
import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import SimpleUserSelector from 'modules/core/components/SimpleUserSelector'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { Assignments } from 'modules/complaints/store/collections/assignments'
import employees from 'modules/employees/store/collections/employees'

type Props = {
  onClose: () => void
}

function iterableAsScalar(iter: Iterable<number>) {
  return JSON.stringify(Array.from(iter).sort())
}

function useOfficersSelector() {
  const assignments = useLazyRef(() => new Assignments()).current
  const { loading } = useAsyncData(() => assignments.fetch(), [])

  const officerIdsFromAssignments = assignments
    .toArray()
    .map((assignment) => assignment.attributes.access_id)

  const [officerIds, setOfficerIds] = React.useState<Set<number>>(
    new Set(officerIdsFromAssignments)
  )

  useEffect(() => {
    setOfficerIds(new Set(officerIdsFromAssignments))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iterableAsScalar(officerIdsFromAssignments)])

  const onOfficerSelected = useUnsafeCallback(
    (id: number | undefined | null) => {
      if (id === null || id === undefined) return

      setOfficerIds((officersWas: Set<number>) => {
        if (!officersWas.delete(id)) {
          officersWas.add(id)
        }
        return new Set(officersWas)
      })
    },
    [setOfficerIds]
  )

  const onMultipleOfficerIdsSelected = useCallback(
    (ids: number[]) => setOfficerIds(new Set(ids)),
    []
  )

  const { running: isSubmitting, performAction } = useAsyncAction((userIds: number[]) =>
    assignments.upsert({ access_ids: userIds })
  )

  const updateOfficers = useCallback(
    () => performAction(Array.from(officerIds)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [iterableAsScalar(officerIds)]
  )

  return {
    assignments,
    loading,
    officerIds: Array.from(officerIds),
    onMultipleOfficerIdsSelected,
    onOfficerSelected,
    updateOfficers,
    isSubmitting,
  }
}

export default function AddOfficerModal({ onClose }: Props) {
  const {
    loading,
    isSubmitting,
    officerIds,
    onOfficerSelected,
    onMultipleOfficerIdsSelected,
    updateOfficers,
  } = useOfficersSelector()

  const handleSubmit = useUnsafeCallback(
    () => updateOfficers().then(onClose),
    [onClose, updateOfficers]
  )

  return (
    <Modal
      title={i18n.t('app_store_v2.apps.complaints.complaints_manager_update.title')}
      description={i18n.t('app_store_v2.apps.complaints.complaints_manager_update.description')}
      illustration="user-male-female"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Button disabled={officerIds.length === 0 || isSubmitting || loading} submit>
          {i18n.t('app_store_v2.apps.complaints.complaints_manager_update.submit')}
        </Button>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <SimpleUserSelector
          loadingOptions={false}
          users={users.models.filter((user) => {
            const employee = user.isNew
              ? undefined
              : employees.find({
                  access_id: user.attributes.id,
                })
            return !user.isTerminated(employee)
          })}
          onSelect={onOfficerSelected}
          onSelectMultiple={onMultipleOfficerIdsSelected}
          selectedIds={officerIds}
        />
      )}
    </Modal>
  )
}
